import React, { useState, useEffect } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler
} from 'chart.js';
import apiClient from '../services/api';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler
);

/**
 * VehicleAnalytics.jsx — Vehicle & Infrastructure Usage Analysis Page
 * Designed for executives to analyze vehicle classifications, traffic patterns, 
 * overweight violations, and estimated road wear impact (ESALs).
 */
const VehicleAnalytics = ({ lang = 'lo', theme = 'dark' }) => {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [data, setData] = useState({
    breakdown: [],
    trend: [],
    hourly: [],
    wimStats: { violations: 0, total_fine: 0, avg_fine: 0 },
    stationStats: []
  });

  const i18n = {
    lo: {
      title: 'ວິເຄາະຂໍ້ມູນລົດ ແລະ ຜົນກະທົບຕໍ່ທາງ',
      subtitle: 'ວິເຄາະປະເພດຍານພາຫະນະ, ການລະເມີດນ້ຳໜັກ WIM ແລະ ດັດຊະນີຄວາມເສຍຫາຍຂອງພື້ນທາງ',
      kpiTotalVehicles: 'ປະລິມານຈະລາຈອນລວມ',
      kpiHeavyRatio: 'ອັດຕາສ່ວນລົດບັນທຸກໜັກ',
      kpiWimViolations: 'ການລະເມີດນ້ຳໜັກ (WIM)',
      kpiRoadWearIndex: 'ດັດຊະນີຄວາມເສຍຫາຍທາງ (ESAL)',
      cardRevenueVsDamage: 'ປຽບທຽບສ່ວນແບ່ງລາຍຮັບ ແລະ ຄວາມເສຍຫາຍ',
      cardRevenueVsDamageSub: 'ສົມທຽບລະຫວ່າງ ສ່ວນແບ່ງລາຍຮັບ (%) ແລະ ອັດຕາການສ້າງຄວາມເສຍຫາຍຕໍ່ທາງ (%) ຕາມປະເພດລົດ',
      cardVehicleShare: 'ສ່ວນແບ່ງປະເພດຍານພາຫະນະ',
      cardVehicleShareSub: 'ແບ່ງຕາມຈຳນວນຄັນ ແລະ ສັດສ່ວນຮ້ອຍລະ',
      cardHourlyPattern: 'ປະລິມານຈະລາຈອນລາຍຊົ່ວໂມງ',
      cardHourlyPatternSub: 'ປຽບທຽບປະລິມານລົດເບົາ ແລະ ລົດໜັກໃນແຕ່ລະຊົ່ວໂມງ',
      cardWimTrend: 'ແນວໂນ້ມ WIM & ຄ່າປັບໄໝ',
      cardWimTrendSub: 'ຈຳນວນຖ້ຽວທີ່ລະເມີດ ແລະ ຄ່າປັບໄໝສະເລ່ຍ',
      cardStationStress: 'ການປະເມີນຄວາມກົດດັນ ແລະ ຄວາມເສຍຫາຍລາຍດ່ານ',
      colStation: 'ດ່ານກວດກາ',
      colTotalTraffic: 'ຈະລາຈອນລວມ',
      colHeavyTraffic: 'ລົດໜັກ (%)',
      colWimViolations: 'ລະເມີດ WIM',
      colRevenue: 'ລາຍຮັບລວມ',
      colRoadWear: 'ດັດຊະນີເສຍຫາຍ',
      lightClass: 'ລົດເບົາ (ເກ໋ງ, ກະບະ, ຕູ້, ຈັກ)',
      mediumClass: 'ລົດກາງ (ບັນທຸກກາງ, ບັດນ້ອຍ)',
      heavyClass: 'ລົດໜັກ (ບັນທຸກໜັກ, ບັດໃຫຍ່)',
      revenueShare: 'ສ່ວນແບ່ງລາຍຮັບ',
      damageShare: 'ອັດຕາສ້າງຄວາມເສຍຫາຍ',
      vehicles: 'ຖ້ຽວ',
      fines: 'ຄ່າປັບໄໝ',
      today: 'ວັນນີ້', day7: '7 ວັນ', day30: '30 ວັນ', day90: '90 ວັນ', all: 'ທັງໝົດ',
      customRange: 'ກຳນົດເອງ',
      fromDate: 'ຈາກວັນທີ',
      toDate: 'ຫາວັນທີ',
      apply: 'ນຳໃຊ້',
    },
    th: {
      title: 'วิเคราะห์ข้อมูลรถและผลกระทบต่อถนน',
      subtitle: 'วิเคราะห์ประเภทยานพาหนะ, การละเมิดน้ำหนัก WIM และดัชนีความเสียหายของพื้นผิวถนน',
      kpiTotalVehicles: 'ปริมาณจราจรรวม',
      kpiHeavyRatio: 'อัตราส่วนรถบรรทุกหนัก',
      kpiWimViolations: 'การละเมิดน้ำหนัก (WIM)',
      kpiRoadWearIndex: 'ดัชนีความเสียหายถนน (ESAL)',
      cardRevenueVsDamage: 'เปรียบเทียบสัดส่วนรายได้ และผลกระทบต่อถนน',
      cardRevenueVsDamageSub: 'เปรียบเทียบระหว่าง สัดส่วนรายได้ (%) และ อัตราการสร้างความเสียหายต่อพื้นทาง (%) ตามประเภทรถ',
      cardVehicleShare: 'สัดส่วนประเภทยานพาหนะ',
      cardVehicleShareSub: 'แบ่งตามจำนวนเที่ยวที่ผ่านทางและสัดส่วนร้อยละ',
      cardHourlyPattern: 'ปริมาณจราจรรายชั่วโมง',
      cardHourlyPatternSub: 'เปรียบเทียบปริมาณรถเบาและรถหนักในแต่ละชั่วโมง',
      cardWimTrend: 'แนวโน้ม WIM & ค่าปรับน้ำหนัก',
      cardWimTrendSub: 'จำนวนเที่ยวที่ผิดกฎหมายและค่าปรับเฉลี่ยรายวัน',
      cardStationStress: 'การประเมินความตึงเครียดและความเสียหายรายด่าน',
      colStation: 'ด่านตรวจ',
      colTotalTraffic: 'จราจรรวม',
      colHeavyTraffic: 'รถหนัก (%)',
      colWimViolations: 'ละเมิด WIM',
      colRevenue: 'รายได้รวม',
      colRoadWear: 'ดัชนีความเสียหาย',
      lightClass: 'รถเบา (เก๋ง, กระบะ, ตู้, มอเตอร์ไซค์)',
      mediumClass: 'รถกลาง (บรรทุกกลาง, บัสเล็ก)',
      heavyClass: 'รถหนัก (บรรทุกใหญ่, บัสใหญ่)',
      revenueShare: 'สัดส่วนรายได้',
      damageShare: 'อัตราสร้างความเสียหาย',
      vehicles: 'เที่ยว',
      fines: 'ค่าปรับ',
      today: 'วันนี้', day7: '7 วัน', day30: '30 วัน', day90: '90 วัน', all: 'ทั้งหมด',
      customRange: 'กำหนดเอง',
      fromDate: 'จากวันที่',
      toDate: 'ถึงวันที่',
      apply: 'นำไปใช้',
    },
    en: {
      title: 'Vehicle & Road Impact Analytics',
      subtitle: 'Vehicle classifications, WIM violations, and road pavement wear-and-tear index',
      kpiTotalVehicles: 'Total Traffic Volume',
      kpiHeavyRatio: 'Heavy Vehicle Ratio',
      kpiWimViolations: 'Weight Violations (WIM)',
      kpiRoadWearIndex: 'Road Damage Index (ESAL)',
      cardRevenueVsDamage: 'Revenue Share vs. Road Damage Contribution',
      cardRevenueVsDamageSub: 'Comparison between revenue contribution (%) and structural road damage (%) by vehicle class',
      cardVehicleShare: 'Vehicle Type Distribution',
      cardVehicleShareSub: 'Based on total traffic counts across all stations',
      cardHourlyPattern: 'Hourly Traffic Flow',
      cardHourlyPatternSub: 'Hourly traffic pattern comparing light vs. heavy vehicles',
      cardWimTrend: 'WIM Violation & Fine Trends',
      cardWimTrendSub: 'Number of weight violations and average fine amount',
      cardStationStress: 'Station Damage & Stress Assessment',
      colStation: 'Station',
      colTotalTraffic: 'Traffic Volume',
      colHeavyTraffic: 'Heavy Traffic (%)',
      colWimViolations: 'WIM Violations',
      colRevenue: 'Total Revenue',
      colRoadWear: 'Wear Index',
      lightClass: 'Light (Car, Pickup, Van, Motorcycle)',
      mediumClass: 'Medium (Small Truck, Small Bus)',
      heavyClass: 'Heavy (Heavy Truck, Large Bus)',
      revenueShare: 'Revenue Share',
      damageShare: 'Road Damage Share',
      vehicles: 'txns',
      fines: 'Fines',
      today: 'Today', day7: '7 Days', day30: '30 Days', day90: '90 Days', all: 'All Time',
      customRange: 'Custom Range',
      fromDate: 'From Date',
      toDate: 'To Date',
      apply: 'Apply',
    }
  };

  const t = (k) => i18n[lang]?.[k] || i18n.en[k] || k;

  // AASHTO 4th Power Law damage multipliers (representing Equivalent Single Axle Loads - ESAL)
  const damageMultipliers = {
    // English mock types
    MOTORCYCLE: 0.00002,
    PASSENGER_CAR: 0.0008,
    VAN: 0.003,
    PICKUP: 0.015,
    TRUCK_SMALL: 0.3,
    BUS_SMALL: 0.25,
    TRUCK_MEDIUM: 2.2,
    BUS_LARGE: 1.8,
    TRUCK_HEAVY: 9.5,

    // Real Lao synced types
    'ລົດເກັ່ງນ້ອຍ (<7 ບ່ອນນັ່ງ), ລົດບັນທຸກຂະຫນາດນ້ອຍ': 0.001,
    'ລົດເກັ່ງນ້ອຍຂະຫນາດນ້ອຍ': 0.0005,
    'entry': 0.0001,
    'exit': 0.0001,
    'TOPUP': 0.00001,
    'ລົດບັນທຸກ 6 ລໍ້': 0.3,
    'ລົດໂດຍສານຂະຫນາດກາງ (12-24 ບ່ອນນັ່ງ)': 0.25,
    'ລົດໂດຍສານຂະຫນາດນ້ອຍ (7-12 ບ່ອນນັ່ງ)': 0.1,
    'ລົດບັນທຸກ 10 ລໍ້ ຂຶ້ນໄປ': 9.5,
    'ລົດບັນທຸກ 10 ລໍ້': 6.0,
    'ລົດໂດຍສານຂະຫນາດໃຫຍ່ (>24 ບ່ອນນັ່ງ)': 1.8,
    'Unknown': 0.01,
  };

  const vehicleNameMap = {
    // English mock types
    MOTORCYCLE: { lo: 'ລົດຈັກ', th: 'รถจักรยานยนต์', en: 'Motorcycle' },
    PASSENGER_CAR: { lo: 'ລົດເກ໋ງ', th: 'รถยนต์นั่ง', en: 'Passenger Car' },
    VAN: { lo: 'ລົດຕູ້', th: 'รถตู้', en: 'Van' },
    PICKUP: { lo: 'ລົດກະບะ', th: 'รถกระบะ', en: 'Pickup' },
    TRUCK_SMALL: { lo: 'ລົດບັນທຸກນ້ອຍ', th: 'รถบรรทุกเล็ก', en: 'Small Truck' },
    TRUCK_MEDIUM: { lo: 'ລົດບັນທຸກກາງ', th: 'รถบรรทุกกลาง', en: 'Medium Truck' },
    TRUCK_HEAVY: { lo: 'ລົດບັນທຸກໜັກ', th: 'รถบรรทุกหนัก', en: 'Heavy Truck' },
    BUS_SMALL: { lo: 'ລົດເມນ້ອຍ', th: 'รถโดยสารเล็ก', en: 'Small Bus' },
    BUS_LARGE: { lo: 'ລົດເມໃຫຍ່', th: 'รถโดยสารใหญ่', en: 'Large Bus' },

    // Real Lao synced types
    'ລົດບັນທຸກ 10 ລໍ້ ຂຶ້ນໄປ': { lo: 'ລົດບັນທຸກ 10 ລໍ້ ຂຶ້ນໄປ', th: 'รถบรรทุก 10 ล้อขึ้นไป', en: 'Heavy Truck (10W+)' },
    'ລົດເກັ່ງນ້ອຍ (<7 ບ່ອນນັ່ງ), ລົດບັນທຸກຂະຫນາດນ້ອຍ': { lo: 'ລົດເກັ່ງນ້ອຍ (<7 ບ່ອນນັ່ງ), ລົດບັນທຸກຂະຫນາດນ້ອຍ', th: 'รถเก๋งเล็ก (<7 ที่นั่ง), รถบรรทุกเล็ก', en: 'Light Vehicle (<7 seats)' },
    'ລົດເກັ່ງນ້ອຍຂະຫນາດນ້ອຍ': { lo: 'ລົດເກັ່ງນ້ອຍຂະຫນາດນ້ອຍ', th: 'รถเก๋งขนาดเล็กพิเศษ', en: 'Mini Passenger Car' },
    'ລົດບັນທຸກ 10 ລໍ້': { lo: 'ລົດບັນທຸກ 10 ລໍ້', th: 'รถบรรทุก 10 ล้อ', en: '10-Wheel Truck' },
    'ລົດໂດຍສານຂະຫນາດກາງ (12-24 ບ່ອນນັ່ງ)': { lo: 'ລົດໂດຍສານຂະຫນາດกาง (12-24 ที่นั่ง)', en: 'Medium Bus (12-24S)' },
    'ລົດໂດຍສານຂະຫນາດນ້ອຍ (7-12 ບ່ອນນັ່ງ)': { lo: 'ລົດໂດຍສານຂະຫນາດเล็ก (7-12 ที่นั่ง)', en: 'Small Bus (7-12S)' },
    'ລົດບັນທຸກ 6 ລໍ້': { lo: 'ລົດບັນທຸກ 6 ລໍ້', th: 'รถบรรทุก 6 ล้อ', en: '6-Wheel Truck' },
    'ລົດໂດຍສານຂະຫນາດໃຫຍ່ (>24 ບ່ອນນັ່ງ)': { lo: 'ລົດໂດຍສານຂະຫນาดใหญ่ (>24 ที่นั่ง)', en: 'Large Bus (>24S)' },
    'entry': { lo: 'ດ່ານທາງເຂົ້າ', th: 'ด่านทางเข้า', en: 'Entry Gate' },
    'exit': { lo: 'ດ່ານທາງອອກ', th: 'ด่านทางออก', en: 'Exit Gate' },
    'TOPUP': { lo: 'ເຕີມເງິນ OBU', th: 'เติมเงิน OBU', en: 'OBU Topup' },
    'Unknown': { lo: 'ບໍ່ຮູ້ປະເພດ', th: 'ไม่ระบุประเภท', en: 'Unknown Class' },
  };

  const getVehicleName = (type) => vehicleNameMap[type]?.[lang] || type;

  const loadVehicleData = async (params = {}) => {
    setLoading(true);
    try {
      const res = await apiClient.dashboardGetVehicleAnalytics(params);
      if (res?.success && res.data) {
        setData(res.data);
      }
    } catch (e) {
      console.warn('Vehicle data load error:', e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (days === 'customRange') return;
    const params = days !== 'all' ? { days } : {};
    loadVehicleData(params);
  }, [days]);

  const handleCustomRangeApply = () => {
    if (!customStart || !customEnd) return;
    setDays('customRange');
    loadVehicleData({ startDate: customStart, endDate: customEnd });
  };

  // Calculations
  const breakdown = data.breakdown || [];
  const totalVehicles = breakdown.reduce((sum, item) => sum + (item.count || 0), 0);
  const totalRevenue = breakdown.reduce((sum, item) => sum + (Number(item.revenue) || 0), 0);

  // Classifications
  const lightTypes = ['MOTORCYCLE', 'PASSENGER_CAR', 'VAN', 'PICKUP', 'ລົດເກັ່ງນ້ອຍ (<7 ບ່ອນນັ່ງ), ລົດບັນທຸກຂະຫນາດນ້ອຍ', 'ລົດເກັ່ງນ້ອຍຂະຫນາດນ້ອຍ', 'entry', 'exit', 'TOPUP'];
  const mediumTypes = ['TRUCK_SMALL', 'BUS_SMALL', 'ລົດບັນທຸກ 6 ລໍ້', 'ລົດໂດຍສານຂະຫນາດກາງ (12-24 ບ່ອນນັ່ງ)', 'ລົດໂດຍສານຂະຫນາດນ້ອຍ (7-12 ບ່ອນນັ່ງ)'];
  const heavyTypes = ['TRUCK_MEDIUM', 'TRUCK_HEAVY', 'BUS_LARGE', 'ລົດບັນທຸກ 10 ລໍ້ ຂຶ້ນໄປ', 'ລົດບັນທຸກ 10 ລໍ້', 'ລົດໂດຍສານຂະຫນາດໃຫຍ່ (>24 ບ່ອນນັ່ງ)'];

  let lightRevenue = 0, lightDamage = 0;
  let mediumRevenue = 0, mediumDamage = 0;
  let heavyCount = 0, heavyRevenue = 0, heavyDamage = 0;

  breakdown.forEach(item => {
    const type = item.vehicle_type;
    const count = item.count || 0;
    const revenue = Number(item.revenue) || 0;
    const mult = damageMultipliers[type] || 0.1;
    const damage = count * mult;

    if (lightTypes.includes(type)) {
      lightRevenue += revenue;
      lightDamage += damage;
    } else if (mediumTypes.includes(type)) {
      mediumRevenue += revenue;
      mediumDamage += damage;
    } else if (heavyTypes.includes(type)) {
      heavyCount += count;
      heavyRevenue += revenue;
      heavyDamage += damage;
    }
  });

  const totalDamage = lightDamage + mediumDamage + heavyDamage;
  const heavyRatio = totalVehicles > 0 ? (heavyCount / totalVehicles * 100) : 0;
  
  // Format helpers
  const fmt = (n) => Number(n || 0).toLocaleString();
  const fmtB = (n) => `₭ ${(Number(n || 0) / 1e9).toFixed(2)}B`;

  // Chart theme configurations
  const gridColor = theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const tickColor = theme === 'dark' ? '#8892a4' : '#546e7a';
  const tooltip = {
    backgroundColor: theme === 'dark' ? 'rgba(30,41,59,0.96)' : 'rgba(255,255,255,0.96)',
    titleColor: theme === 'dark' ? '#f8fafc' : '#0f172a',
    bodyColor: theme === 'dark' ? '#94a3b8' : '#475569',
    borderColor: theme === 'dark' ? '#334155' : '#e2e8f0',
    borderWidth: 1,
    padding: 12
  };

  // 1. Chart: Revenue Share vs Road Damage Share
  const totalRevCalc = (lightRevenue + mediumRevenue + heavyRevenue) || 1;
  const totalDmgCalc = totalDamage || 1;

  const compareData = {
    labels: [t('lightClass'), t('mediumClass'), t('heavyClass')],
    datasets: [
      {
        label: t('revenueShare'),
        data: [
          (lightRevenue / totalRevCalc * 100).toFixed(1),
          (mediumRevenue / totalRevCalc * 100).toFixed(1),
          (heavyRevenue / totalRevCalc * 100).toFixed(1)
        ],
        backgroundColor: '#1890ff',
        borderRadius: 4,
      },
      {
        label: t('damageShare'),
        data: [
          (lightDamage / totalDmgCalc * 100).toFixed(1),
          (mediumDamage / totalDmgCalc * 100).toFixed(1),
          (heavyDamage / totalDmgCalc * 100).toFixed(1)
        ],
        backgroundColor: '#f5222d',
        borderRadius: 4,
      }
    ]
  };

  const compareOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: { color: tickColor, font: { size: 11, family: 'var(--font)' } }
      },
      tooltip
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: tickColor, font: { size: 10, family: 'var(--font)' } } },
      y: { grid: { color: gridColor }, ticks: { color: tickColor, callback: v => `${v}%`, font: { size: 10 } } }
    }
  };

  // 2. Chart: Vehicle Classification distribution
  const shareColors = ['#1890ff', '#fa8c16', '#faad14', '#52c41a', '#13c2c2', '#2f54eb', '#722ed1', '#eb2f96', '#9254de'];
  const shareData = {
    labels: breakdown.map(item => getVehicleName(item.vehicle_type)),
    datasets: [{
      data: breakdown.map(item => item.count),
      backgroundColor: shareColors.slice(0, breakdown.length),
      borderWidth: 0,
      hoverOffset: 6
    }]
  };

  const shareOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tooltip,
        callbacks: {
          label: (ctx) => {
            const val = ctx.raw;
            const pct = totalVehicles > 0 ? (val / totalVehicles * 100).toFixed(1) : 0;
            return `  ${ctx.label}: ${fmt(val)} ${t('vehicles')} (${pct}%)`;
          }
        }
      }
    },
    cutout: '70%'
  };

  // 3. Chart: Hourly traffic flow (Light vs Heavy)
  const hourlyFlow = data.hourly || [];
  const hourlyData = {
    labels: hourlyFlow.map(h => `${String(h.hour).padStart(2, '0')}:00`),
    datasets: [
      {
        label: lang === 'lo' ? 'ລົດເບົາ' : lang === 'th' ? 'รถเบา' : 'Light Vehicles',
        data: hourlyFlow.map(h => h.light_count),
        borderColor: '#1890ff',
        backgroundColor: 'rgba(24,144,255,0.05)',
        fill: true,
        tension: 0.3,
        borderWidth: 2,
        pointRadius: 1
      },
      {
        label: lang === 'lo' ? 'ລົດໜັກ' : lang === 'th' ? 'รถหนัก' : 'Heavy Vehicles',
        data: hourlyFlow.map(h => h.heavy_count),
        borderColor: '#f5222d',
        backgroundColor: 'rgba(245,34,45,0.05)',
        fill: true,
        tension: 0.3,
        borderWidth: 2,
        pointRadius: 1
      }
    ]
  };

  const hourlyOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: { color: tickColor, font: { size: 10, family: 'var(--font)' } }
      },
      tooltip
    },
    scales: {
      x: { grid: { color: gridColor }, ticks: { color: tickColor, font: { size: 9 } } },
      y: { grid: { color: gridColor }, ticks: { color: tickColor, font: { size: 9 } } }
    }
  };

  // 4. Chart: WIM Overweight Violations and Fines Trend
  const timelineData = data.trend || [];
  const wimTrendList = data.wimTrend || [];
  
  // Create a map of date -> WIM stats
  const wimMap = {};
  wimTrendList.forEach(w => {
    // format key: w.day matches d.day format (e.g. YYYY-MM-DD)
    wimMap[w.day] = {
      count: w.count || 0,
      avg_fine: w.avg_fine || 0
    };
  });

  const violationTrend = {
    labels: timelineData.map(d => {
      const parts = d.day.split('-');
      return parts.length === 3 ? `${parts[1]}/${parts[2]}` : d.day;
    }),
    datasets: [
      {
        label: t('kpiWimViolations'),
        data: timelineData.map(d => wimMap[d.day]?.count || 0),
        backgroundColor: 'rgba(245, 34, 45, 0.75)',
        yAxisID: 'y1',
        borderRadius: 3,
        barPercentage: 0.6
      },
      {
        label: lang === 'lo' ? 'ຄ່າປັບສະເລ່ຍ' : lang === 'th' ? 'ค่าปรับเฉลี่ย' : 'Avg Fine',
        data: timelineData.map(d => wimMap[d.day]?.avg_fine || 0),
        borderColor: '#fa8c16',
        borderWidth: 2,
        pointRadius: 2,
        fill: false,
        tension: 0.2,
        type: 'line',
        yAxisID: 'y2'
      }
    ]
  };

  const violationOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: { color: tickColor, font: { size: 10, family: 'var(--font)' } }
      },
      tooltip
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: tickColor, font: { size: 9 } } },
      y1: {
        type: 'linear',
        position: 'left',
        grid: { color: gridColor },
        ticks: { color: tickColor, font: { size: 9 } }
      },
      y2: {
        type: 'linear',
        position: 'right',
        grid: { display: false },
        ticks: {
          color: tickColor,
          font: { size: 9 },
          callback: v => `₭${(v/1e3).toFixed(0)}k`
        }
      }
    }
  };

  // Station processing
  const stationStats = (data.stationStats || []).map(st => {
    const heavyRatio = st.total_vehicles > 0 ? (st.heavy_vehicles / st.total_vehicles) : 0;
    const estimatedWear = (st.heavy_vehicles * 9.5) + ((st.total_vehicles - st.heavy_vehicles) * 0.02);
    return {
      ...st,
      heavyRatio: (heavyRatio * 100).toFixed(1),
      roadWearIndex: Math.round(estimatedWear)
    };
  });

  return (
    <div className="main">
      <div className="section-header" style={{ flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div className="section-title">{t('title')}</div>
          <div className="section-sub">{t('subtitle')}</div>
        </div>
        
        {/* Filters */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {[{ v: 1, l: t('today') }, { v: 7, l: t('day7') }, { v: 30, l: t('day30') }, { v: 90, l: t('day90') }, { v: 'all', l: t('all') }].map(d => (
            <div key={d.v} className={`filter-chip ${days === d.v ? 'active' : ''}`}
              onClick={() => setDays(d.v)} style={{ cursor: 'pointer' }}>
              {d.l}
            </div>
          ))}
          <div style={{ width: '1px', height: '20px', background: 'var(--border)', margin: '0 8px' }} />
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text3)' }}>{t('fromDate')}:</span>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                style={{
                  background: 'var(--bg3)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  color: 'var(--text)',
                  fontFamily: 'var(--font)',
                  outline: 'none',
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text3)' }}>{t('toDate')}:</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                style={{
                  background: 'var(--bg3)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  color: 'var(--text)',
                  fontFamily: 'var(--font)',
                  outline: 'none',
                }}
              />
            </div>
            <button
              onClick={handleCustomRangeApply}
              disabled={!customStart || !customEnd}
              style={{
                background: 'var(--blue)',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                padding: '5px 12px',
                fontSize: '12px',
                cursor: (!customStart || !customEnd) ? 'not-allowed' : 'pointer',
                opacity: (!customStart || !customEnd) ? 0.5 : 1,
                fontFamily: 'var(--font)',
                fontWeight: '600',
                transition: 'all 0.2s ease',
              }}
            >
              {t('apply')}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '80px 0', textAlign: 'center', color: 'var(--text3)' }}>
          <div style={{
            display: 'inline-block', width: '28px', height: '28px',
            border: '3px solid var(--blue)', borderTopColor: 'transparent',
            borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginBottom: '12px'
          }} />
          <div>Retrieving vehicle metrics...</div>
        </div>
      ) : (
        <>
          {/* KPI Dashboard */}
          <div className="kpi-grid" style={{ marginBottom: '16px' }}>
            <div className="kpi">
              <div className="kpi-accent" style={{ background: '#1890ff' }} />
              <div className="kpi-label">{t('kpiTotalVehicles')}</div>
              <div className="kpi-value" style={{ color: '#1890ff' }}>{fmt(totalVehicles)}</div>
              <div className="kpi-sub" style={{ color: 'var(--text3)' }}>{fmtB(totalRevenue)} revenue</div>
            </div>
            <div className="kpi">
              <div className="kpi-accent" style={{ background: '#9254de' }} />
              <div className="kpi-label">{t('kpiHeavyRatio')}</div>
              <div className="kpi-value" style={{ color: '#9254de' }}>{heavyRatio.toFixed(1)}%</div>
              <div className="kpi-sub" style={{ color: 'var(--text3)' }}>{fmt(heavyCount)} {t('vehicles')}</div>
            </div>
            <div className="kpi">
              <div className="kpi-accent" style={{ background: '#f5222d' }} />
              <div className="kpi-label">{t('kpiWimViolations')}</div>
              <div className="kpi-value" style={{ color: '#f5222d' }}>{fmt(data.wimStats?.violations)}</div>
              <div className="kpi-sub" style={{ color: 'var(--text3)' }}>{fmtB(data.wimStats?.total_fine)} collected</div>
            </div>
            <div className="kpi">
              <div className="kpi-accent" style={{ background: '#fa8c16' }} />
              <div className="kpi-label">{t('kpiRoadWearIndex')}</div>
              <div className="kpi-value" style={{ color: '#fa8c16' }}>{fmt(totalDamage)}</div>
              <div className="kpi-sub" style={{ color: 'var(--text3)' }}>Cumulative ESAL wear</div>
            </div>
          </div>

          {/* Row 1: Pavement Wear Impact vs. Revenue & Vehicle Share */}
          <div className="chart-row" style={{ marginBottom: '16px' }}>
            <div className="card" style={{ flex: 1.5 }}>
              <div className="card-title">{t('cardRevenueVsDamage')}</div>
              <div className="card-sub">{t('cardRevenueVsDamageSub')}</div>
              <div style={{ height: '220px', marginTop: '16px' }}>
                <Bar data={compareData} options={compareOptions} />
              </div>
            </div>

            <div className="card" style={{ flex: 1 }}>
              <div className="card-title">{t('cardVehicleShare')}</div>
              <div className="card-sub">{t('cardVehicleShareSub')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.8fr', gap: '12px', alignItems: 'center', marginTop: '16px' }}>
                <div style={{ height: '140px' }}>
                  <Doughnut data={shareData} options={shareOptions} />
                </div>
                <div style={{ maxHeight: '160px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px', paddingRight: '4px' }}>
                  {breakdown.slice(0, 7).map((item, idx) => {
                    const pct = totalVehicles > 0 ? ((item.count / totalVehicles) * 100).toFixed(1) : '0';
                    const color = shareColors[idx % shareColors.length];
                    return (
                      <div key={item.vehicle_type} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px' }}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: color, flexShrink: 0 }} />
                        <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text2)' }}>
                          {getVehicleName(item.vehicle_type)}
                        </span>
                        <span style={{ color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: '10px' }}>{fmt(item.count)}</span>
                        <span style={{ color, fontWeight: '600', fontFamily: 'var(--mono)', fontSize: '10px', width: '36px', textAlign: 'right' }}>{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Hourly Traffic Flows & WIM Trends */}
          <div className="chart-row" style={{ marginBottom: '16px' }}>
            <div className="card">
              <div className="card-title">{t('cardHourlyPattern')}</div>
              <div className="card-sub">{t('cardHourlyPatternSub')}</div>
              <div style={{ height: '200px', marginTop: '16px' }}>
                <Line data={hourlyData} options={hourlyOptions} />
              </div>
            </div>

            <div className="card">
              <div className="card-title">{t('cardWimTrend')}</div>
              <div className="card-sub">{t('cardWimTrendSub')}</div>
              <div style={{ height: '200px', marginTop: '16px' }}>
                <Bar data={timelineData.length > 0 ? violationTrend : { labels: [], datasets: [] }} options={violationOptions} />
              </div>
            </div>
          </div>

          {/* Row 3: Station Impact Table */}
          <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: '16px' }}>
            <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div className="card-title">{t('cardStationStress')}</div>
                <div className="card-sub" style={{ marginTop: '2px' }}>Structural road impact analysis by inspection gate</div>
              </div>
              <span style={{ padding: '4px 8px', background: 'rgba(250,140,22,.12)', color: '#fa8c16', borderRadius: '4px', fontSize: '10px', fontWeight: '700' }}>
                AASHTO DAMAGE ASSESSMENT
              </span>
            </div>
            
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('colStation')}</th>
                  <th style={{ textAlign: 'right' }}>{t('colTotalTraffic')}</th>
                  <th style={{ textAlign: 'right' }}>{t('colHeavyTraffic')}</th>
                  <th style={{ textAlign: 'right' }}>{t('colWimViolations')}</th>
                  <th style={{ textAlign: 'right' }}>{t('colRevenue')}</th>
                  <th style={{ textAlign: 'right' }}>{t('colRoadWear')}</th>
                  <th style={{ width: '130px' }}>Stress Gauge</th>
                </tr>
              </thead>
              <tbody>
                {stationStats.map(st => {
                  const maxWear = Math.max(1, ...stationStats.map(s => s.roadWearIndex));
                  const wearPct = Math.min(100, Math.round((st.roadWearIndex / maxWear) * 100));
                  const stressColor = wearPct > 75 ? '#f5222d' : wearPct > 40 ? '#fa8c16' : '#52c41a';
                  
                  return (
                    <tr key={st.station_id}>
                      <td style={{ fontWeight: '600' }}>
                        {lang === 'lo' ? st.name_la : st.name_en}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--mono)' }}>{fmt(st.total_vehicles)}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--mono)' }}>
                        {st.heavy_vehicles > 0 ? (
                          <span>{st.heavyRatio}% <span style={{ color: 'var(--text3)', fontSize: '10px' }}>({fmt(st.heavy_vehicles)})</span></span>
                        ) : '0.0%'}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', color: st.wim_violations > 0 ? '#f5222d' : 'var(--text2)' }}>
                        {st.wim_violations > 0 ? `⚠️ ${fmt(st.wim_violations)}` : '-'}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: '600', color: 'var(--teal)' }}>
                        {fmtB(st.total_revenue)}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: '600', color: stressColor }}>
                        {fmt(st.roadWearIndex)}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <div style={{ flex: 1, height: '6px', borderRadius: '3px', background: 'var(--bg4)', overflow: 'hidden' }}>
                            <div style={{ height: '100%', borderRadius: '3px', background: stressColor, width: `${wearPct}%`, transition: 'width 0.6s ease' }} />
                          </div>
                          <span style={{ fontSize: '10px', color: stressColor, fontWeight: '700', width: '28px', textAlign: 'right' }}>
                            {wearPct}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default VehicleAnalytics;
